import { createContext, useContext, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import { CreateSaleOrderDto, SaleOrderResponseDto } from '@bandido/shared/dtos';

import { QueryKeys } from '@frontend/features/common/types';
import { useSnackBar } from '@frontend/features/common/components/SnackBarContext';

import { addSaleOrderItem, convertCartToSaleOrder, fetchSaleOrder, removeItemFromSaleOrder } from './services';

export const CartContext = createContext<{
  state?: SaleOrderResponseDto | null;
  isLoading: boolean;
  isSaving: boolean;
  createOrderIsLoading: boolean;
  addItem: (payload: CreateSaleOrderDto & { socketId: string }) => Promise<SaleOrderResponseDto>;
  removeItem: (itemId: string) => Promise<void>;
  refetch: () => void;
  convert: () => Promise<SaleOrderResponseDto | null>;
    }>({
      state: null,
      isLoading: false,
      isSaving: false,
      createOrderIsLoading: false,
      refetch: () => {},
      removeItem: async () => {},
      addItem: async () => ({}) as SaleOrderResponseDto,
      convert: async () => ({}) as SaleOrderResponseDto,
    });

interface Props {
  children: JSX.Element;
}

export const CartContextProvider = ({ children }: Props) => {
  const { addToast } = useSnackBar();
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();
  const { data: state, isLoading, refetch } = useQuery<SaleOrderResponseDto>([QueryKeys.saleOrder], () => fetchSaleOrder());
  const queryClient = useQueryClient();

  const addItemMutation = useMutation((payload: CreateSaleOrderDto & { socketId: string }) => addSaleOrderItem(payload));

  const removeItemMutation = useMutation((itemId: string) => removeItemFromSaleOrder(itemId), {
    onSuccess: async () => {
      await queryClient.invalidateQueries([QueryKeys.saleOrder]);
    },
  });

  const addItem = async (payload: CreateSaleOrderDto & { socketId: string }) => {
    return addItemMutation.mutateAsync(payload);
  };

  const removeItem = async (itemId: string) => {
    await removeItemMutation.mutateAsync(itemId);
  };

  const convertCartToSaleOrderMutation = useMutation((id: string) => convertCartToSaleOrder(id), {
    onSettled: () => {
      setIsSaving(false);
    },
    onMutate: () => {
      setIsSaving(true);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([QueryKeys.saleOrder]);
      addToast({
        color: 'success',
        message: t('cart.convert.success')
      });
    },
    onError: () => {
      addToast({
        color: 'error',
        message: t('cart.convert.error')
      });
    },
  });

  const convert = async () => {
    if(state?.id) {
      return convertCartToSaleOrderMutation.mutateAsync(state.id);
    }
    return null;
  };

  return (
    <CartContext.Provider
      value={{
        state,
        isLoading,
        isSaving,
        addItem,
        removeItem,
        refetch,
        convert,
        createOrderIsLoading: addItemMutation.isLoading,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartContextProvider');
  }
  return context;
};
